/**
 * useTendrilChat — hook managing Tendril chat state.
 *
 * Manages:
 *   - conversationId: stable UUID generated once per mount (useRef)
 *   - messages: array of { role, content, pendingAction?, uiActions? }
 *   - loading: bool
 *   - error: string | null
 *
 * API calls: uses apiClient (existing auth + traceparent infrastructure) for
 * the buffered `/api/tendril/chat` path, and a direct `fetch` against the
 * streaming Function URL (tendril-streaming-responses Phase 3, WI-007) when
 * streaming is enabled.
 *
 * Security: never log message content or JWT — only operational metrics.
 */
import { useState, useRef, useCallback } from 'react';
// local-dev-environment Phase 2 remediation (finding #5): route through the
// authClient seam (cognito | local via VITE_AUTH_MODE) instead of calling
// `aws-amplify/auth` directly.
import { authClient } from '../auth/authClient.js';
import { apiClient } from '../api/client.js';
import { getSessionTraceparent } from '../lib/tendril/trace.js';
import { getActiveTraceparent, TRACEPARENT_HEADER } from '../lib/tracing.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { consumeTendrilStream, isNdjsonStreamResponse } from './useTendrilStream.ts';
import { capabilityLabel, labelFromStatusText } from '../lib/tendril/capabilityLabel.js';

/**
 * Friendly, actionable fallback shown to the user when a Tendril request
 * fails. Session-expiry gets its own clearer copy; everything else (server
 * errors, network failures, unexpected exceptions) collapses to this single
 * graceful message so the user is never shown a raw/internal error string.
 */
const GENERIC_CHAT_ERROR =
  "Sorry — I couldn't complete that request. Please try again, or rephrase/narrow what you're asking for.";

/**
 * ISSUE 2 fix: a confirm failure caused by an OpCo write-scope/permission
 * denial (backend responds with body.error === 'scope_denied' — see
 * backend/src/handlers/tendril.ts's /confirm 403 branch) is not something the
 * user can fix by retrying, so it gets its own copy instead of the generic
 * "try again" message.
 */
const SCOPE_DENIED_CHAT_ERROR =
  "That OpCo isn't in your assigned operating groups, so you can't edit its initiatives — I can still show or analyze it for you.";

/**
 * Render a user-facing error message for a failed /chat or /confirm call.
 * Deliberately does NOT surface `err.message` verbatim — backend error text
 * may change independently of the UI, and raw messages are not always
 * actionable for the end user. Session-expiry and scope-denial each get their
 * own clearer copy, since the user's next action differs from "try again":
 *   - session-expiry: sign in again
 *   - scope-denial: nothing to retry — the OpCo is out of the caller's write
 *     scope; offer to show/analyze instead
 * Everything else (server errors, network failures, unexpected exceptions,
 * other 4xx) collapses to the single generic graceful message.
 *
 * @param {unknown} err
 * @returns {string}
 */
export function friendlyChatErrorText(err) {
  if (err && typeof err === 'object' && 'sessionExpired' in err && err.sessionExpired) {
    return 'Your session has expired. Please sign in again.';
  }
  if (err && typeof err === 'object' && err.body && typeof err.body === 'object' && err.body.error === 'scope_denied') {
    return SCOPE_DENIED_CHAT_ERROR;
  }
  return GENERIC_CHAT_ERROR;
}

/**
 * Generate a simple UUID v4.
 * Uses crypto.randomUUID when available (modern browsers).
 *
 * @returns {string} UUID v4 string
 */
function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Every message gets a stable id so streaming updates can target it precisely. */
function nextMessageId() {
  return generateUuid();
}

/**
 * Same-origin path for the streaming endpoint.
 *
 * This is deliberately a constant, NOT a configurable URL. The tendril-stream
 * Lambda is reached through an `ordered_cache_behavior` on the portal's own
 * CloudFront distribution (`path_pattern = "/api/tendril-stream/*"`), so the
 * endpoint is same-origin with the app and therefore identical in every
 * environment — there is nothing to configure and nothing to drift.
 *
 * Making this configurable would be actively harmful: the WAF web ACL is
 * attached to the CloudFront *distribution* with a scope-down statement on
 * `uri_path STARTS_WITH "/api/tendril-stream/"`. A build pointed at the raw
 * Function URL would bypass CloudFront entirely and therefore bypass the rate
 * limit, silently turning the edge protection into decoration. Hardcoding the
 * relative path makes that bypass impossible rather than one env var away.
 *
 * Any path under `/api/tendril-stream/*` reaches the handler — it ignores the
 * incoming `rawPath` and builds a synthetic `/api/tendril/chat` event for the
 * reused PAT/JWT authorizer.
 *
 * Before the CloudFront behavior exists (it is gated on
 * `tendril_stream_function_url_domain_name`, populated on a second apply),
 * this path falls through to the SPA fallback and returns `200 text/html`.
 * That is handled safely: `isStreamEstablished` requires both 2xx AND
 * `application/x-ndjson`, so an HTML fallback counts as "never started" and
 * the turn silently uses the buffered endpoint.
 */
const TENDRIL_STREAM_PATH = '/api/tendril-stream/chat';

/**
 * Streaming is opt-in via a single env var (the rollout flag). The endpoint
 * itself is not configurable — see TENDRIL_STREAM_PATH. Read fresh on every
 * call (not cached at module scope) so tests can flip the flag between cases
 * via `vi.stubEnv` + `vi.resetModules()`.
 */
function isStreamingEnabled() {
  return import.meta.env.VITE_TENDRIL_STREAMING_ENABLED === 'true';
}

/**
 * @typedef {{
 *   id?: string,
 *   role: 'user' | 'assistant',
 *   content: string,
 *   pendingAction?: object,
 *   uiActions?: object[],
 *   isError?: boolean,
 *   isStreaming?: boolean,
 *   isDegraded?: boolean,
 *   degradedReason?: 'soft_deadline' | 'hard_ceiling' | null,
 *   errorKind?: 'stream_error' | 'stall' | null,
 *   outcome?: 'ok' | 'degraded' | 'aborted' | 'error' | null,
 *   originalUserText?: string,
 * }} ChatMessage
 */

/**
 * @typedef {{ outcome: 'ok' | 'degraded' | 'aborted' | 'error' }} SendMessageResult
 */

/**
 * Tendril chat hook.
 *
 * @returns {{
 *   messages: ChatMessage[],
 *   loading: boolean,
 *   error: string | null,
 *   pendingAction: object | null,
 *   conversationId: string,
 *   sendMessage: (text: string, pageContext?: { url: string }) => Promise<SendMessageResult>,
 *   sendConfirm: () => Promise<void>,
 *   cancelPendingAction: () => void,
 *   clearChat: () => void,
 * }}
 */
export function useTendrilChat() {
  const { user, profile } = useAuth();
  const conversationIdRef = useRef(generateUuid());
  const [messages, setMessages] = useState(/** @type {ChatMessage[]} */ ([]));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  /**
   * Build the history array for the API from current messages.
   *
   * JD-7 fix: excludes `isError: true` messages entirely (a synthetic UI
   * fallback string is not a real prior answer — sending it back to the
   * model as history corrupts the transcript). A degraded (partial) prior
   * assistant turn is marked `partial: true` on its history entry so the
   * model knows that turn did not fully complete — structurally, not just
   * via prose.
   */
  const buildHistory = useCallback((msgs) => {
    return msgs
      .filter((m) => !m.isError)
      .map((m) => {
        const entry = { role: m.role, content: m.content };
        if (m.isDegraded) entry.partial = true;
        return entry;
      });
  }, []);

  const updateMessageById = useCallback((id, updater) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  }, []);

  const buildUserContext = useCallback(() => {
    const userContext = {};
    if (user?.email) userContext.email = user.email;
    if (user?.title) userContext.title = user.title;
    if (user?.opcoId) userContext.companyId = user.opcoId;
    if (typeof profile?.opcoName === 'string' && profile.opcoName) {
      userContext.companyName = profile.opcoName;
    }
    if (typeof profile?.role === 'string' && profile.role) {
      userContext.role = profile.role;
    }
    return userContext;
  }, [user, profile]);

  /**
   * Sends the turn through the existing buffered `/api/tendril/chat` path.
   * Used both as the primary path (streaming disabled) and as the automatic
   * fallback when a streaming attempt never started (AC-ERROR-2).
   *
   * @returns {Promise<SendMessageResult>}
   */
  const sendViaBufferedPath = useCallback(
    async (trimmed, pageContext, historyForRequest) => {
      try {
        const sessionTraceparent = getSessionTraceparent();
        const userContext = buildUserContext();

        const response = await apiClient.post(
          '/api/tendril/chat',
          {
            conversationId: conversationIdRef.current,
            message: trimmed,
            history: historyForRequest,
            pageContext,
            ...(Object.keys(userContext).length > 0 && { userContext }),
          },
          // W3C traceparent sent as an HTTP request header (not in the body)
          // so the backend can read it from event.headers per W3C Trace Context spec.
          sessionTraceparent ? { headers: { traceparent: sessionTraceparent } } : undefined,
        );

        const assistantMsg = {
          id: nextMessageId(),
          role: 'assistant',
          content: response.reply,
          pendingAction: response.pendingAction ?? null,
          uiActions: response.ui_actions ?? [],
          outcome: 'ok',
        };

        setMessages((prev) => [...prev, assistantMsg]);

        if (response.pendingAction) {
          setPendingAction(response.pendingAction);
        }
        return { outcome: 'ok' };
      } catch (err) {
        const errorText = friendlyChatErrorText(err);

        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), role: 'assistant', content: errorText, isError: true, outcome: 'error', originalUserText: trimmed },
        ]);
        setError(errorText);
        return { outcome: 'error' };
      }
    },
    [buildUserContext],
  );

  /**
   * Attempts the streaming transport for one turn. Returns `{ started:
   * false }` when the stream never actually began (network failure before
   * any bytes, or a pre-stream 401/409 that resolved without ever entering
   * ndjson content) — the caller falls back to the buffered path for the
   * SAME turn in that case. Returns `{ started: true, outcome }` once a
   * stream did start and reached a terminal state.
   *
   * `turn_already_claimed` recovery (judgment call, not pinned by the
   * creative doc): rather than surfacing a generic retryable error — which
   * would risk the user re-submitting into the same already-claimed turnId
   * and looping forever — this generates a FRESH turnId and automatically
   * resubmits the same user text once, silently. The stale placeholder
   * message is removed first so no dead assistant bubble is left behind.
   * If the RETRIED attempt (with its own fresh turnId) also collides, this
   * is bounded by `hasRetriedClaim`: it does NOT recurse again — it
   * surfaces as a normal terminal `errorKind: 'stream_error'`, same as any
   * other mid-stream error frame handled above.
   *
   * @returns {Promise<{ started: boolean, outcome?: 'ok' | 'degraded' | 'aborted' | 'error' }>}
   */
  const attemptStreaming = useCallback(
    async (trimmed, pageContext, historyForRequest, turnId, hasRetriedClaim = false) => {
      const streamUrl = TENDRIL_STREAM_PATH;

      let idToken = null;
      try {
        const session = await authClient.fetchAuthSession();
        idToken = session?.tokens?.idToken?.toString() ?? null;
      } catch {
        idToken = null;
      }
      if (!idToken) {
        // Treat an unobtainable auth token exactly like a pre-stream auth
        // failure — never throw uncaught into the panel, just fall back.
        return { started: false };
      }

      const activeTraceparent = getActiveTraceparent();

      let response;
      try {
        response = await fetch(streamUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
            ...(activeTraceparent ? { [TRACEPARENT_HEADER]: activeTraceparent } : {}),
          },
          body: JSON.stringify({
            conversationId: conversationIdRef.current,
            message: trimmed,
            turnId,
            history: historyForRequest,
            pageContext,
            protocolVersions: ['1'],
          }),
        });
      } catch {
        // Network failure before any bytes — never started.
        return { started: false };
      }

      if (!isNdjsonStreamResponse(response)) {
        // 401/409/etc pre-stream — never started (AC-ERROR-2 distinguishing signal).
        return { started: false };
      }

      const messageId = nextMessageId();
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          role: 'assistant',
          content: '',
          statusLines: [],
          // Turn-level timing. The collapsed chip used to derive its duration
          // from the first and last STATUS line, which measured only the
          // tool-calling phase — the first live UAT showed "· 5s" for a turn the
          // Lambda reported as 12.9s. These bracket the whole turn instead.
          turnStartedAt: Date.now(),
          turnEndedAt: null,
          isStreaming: true,
          isDegraded: false,
          degradedReason: null,
          errorKind: null,
          outcome: null,
          pendingAction: null,
          uiActions: [],
          originalUserText: trimmed,
        },
      ]);

      let pendingActionPayload = null;
      let uiActionsPayload = [];
      let alreadyClaimed = false;
      let doneOutcome = null;
      let sawLocalErrorKind = null;

      await consumeTendrilStream(response, {
        // A capability produces TWO frames — `status` when it starts, `tool`
        // when it finishes — but they are ONE logical step. The first
        // implementation appended a separate line for each, so a 2-capability
        // turn rendered four entries ("Running list_workshops…" then
        // "list_workshops") and the collapsed chip claimed "Checked 4 things".
        // Now `status` appends the step and `tool` RESOLVES it in place.
        onStatus: (frame) => {
          const { label, capability } = labelFromStatusText(frame.text);
          updateMessageById(messageId, (m) => ({
            ...m,
            statusLines: [
              ...(m.statusLines ?? []),
              {
                id: `step-${frame.seq}`,
                label,
                capability,
                status: 'active',
                startedAt: Date.now(),
                endedAt: null,
              },
            ],
          }));
        },
        onTool: (frame) => {
          updateMessageById(messageId, (m) => {
            const lines = m.statusLines ?? [];
            // Resolve the most recent still-active step for this capability;
            // fall back to the most recent active step of any capability, since
            // a server-authored status text we could not parse has no
            // capability recorded against it.
            let idx = -1;
            for (let k = lines.length - 1; k >= 0; k -= 1) {
              if (lines[k].status === 'active' && lines[k].capability === frame.capability) { idx = k; break; }
            }
            if (idx === -1) {
              for (let k = lines.length - 1; k >= 0; k -= 1) {
                if (lines[k].status === 'active') { idx = k; break; }
              }
            }

            // No active step to resolve (e.g. a tool frame with no preceding
            // status frame): append a completed one so the step is still shown.
            if (idx === -1) {
              return {
                ...m,
                statusLines: [
                  ...lines,
                  {
                    id: `step-${frame.seq}`,
                    label: capabilityLabel(frame.capability),
                    capability: frame.capability,
                    status: 'done',
                    startedAt: Date.now(),
                    endedAt: Date.now(),
                  },
                ],
              };
            }

            const next = [...lines];
            next[idx] = { ...next[idx], status: 'done', endedAt: Date.now() };
            return { ...m, statusLines: next };
          });
        },
        onToken: (frame) => {
          updateMessageById(messageId, (m) => ({
            ...m,
            content: frame.text,
            // First token means the tool phase is over — resolve anything still
            // showing as active so no step is left spinning forever.
            statusLines: (m.statusLines ?? []).map((s) =>
              s.status === 'active' ? { ...s, status: 'done', endedAt: s.endedAt ?? Date.now() } : s,
            ),
          }));
        },
        onPendingAction: (frame) => {
          // Stored on arrival, but the turn is only treated as interactable
          // once `done` arrives (see onDone below) — pending_action is
          // terminal-adjacent, not itself terminal.
          pendingActionPayload = frame.pendingAction;
        },
        onUiActions: (frame) => {
          uiActionsPayload = frame.actions;
        },
        onDegraded: (frame) => {
          updateMessageById(messageId, (m) => ({ ...m, isDegraded: true, degradedReason: frame.cause }));
        },
        onError: (frame) => {
          if (frame.code === 'turn_already_claimed') {
            alreadyClaimed = true;
            return;
          }
          sawLocalErrorKind = 'stream_error';
          updateMessageById(messageId, (m) => ({ ...m, errorKind: 'stream_error', isStreaming: false, turnEndedAt: Date.now() }));
        },
        onProtocolError: () => {
          sawLocalErrorKind = 'stream_error';
          updateMessageById(messageId, (m) => ({ ...m, errorKind: 'stream_error', isStreaming: false, turnEndedAt: Date.now() }));
        },
        onStall: () => {
          sawLocalErrorKind = 'stall';
          updateMessageById(messageId, (m) => ({ ...m, errorKind: 'stall', isStreaming: false, turnEndedAt: Date.now() }));
        },
        onDone: (frame) => {
          doneOutcome = frame.outcome;
          if (alreadyClaimed) return; // handled after consume resolves — never mark this stale message terminal-ok.
          updateMessageById(messageId, (m) => ({
            ...m,
            isStreaming: false, turnEndedAt: Date.now(),
            outcome: frame.outcome,
            pendingAction: pendingActionPayload,
            uiActions: uiActionsPayload,
          }));
          if (pendingActionPayload) setPendingAction(pendingActionPayload);
        },
      });

      if (alreadyClaimed) {
        if (hasRetriedClaim) {
          // Second consecutive collision on the SAME logical send — do not
          // recurse again. Surface as a normal terminal stream error,
          // matching how a genuine mid-stream error frame is handled above.
          updateMessageById(messageId, (m) => ({ ...m, errorKind: 'stream_error', isStreaming: false, turnEndedAt: Date.now() }));
          return { started: true, outcome: 'error' };
        }
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        const freshTurnId = generateUuid();
        return attemptStreaming(trimmed, pageContext, historyForRequest, freshTurnId, true);
      }

      const outcome = doneOutcome ?? (sawLocalErrorKind ? 'error' : 'error');
      return { started: true, outcome };
    },
    [updateMessageById],
  );

  /**
   * Send a user message. Streaming (when enabled) is attempted first; a
   * per-turn, non-sticky automatic fallback to the buffered path covers any
   * case where the stream never actually started (AC-ERROR-2). Resolves an
   * outcome the caller (TendrilChatPanel) uses to decide whether to clear
   * or restore the composer (JD-8) — never inspects DOM state itself.
   *
   * @param {string} text
   * @param {{ url: string }} [pageContext]
   * @returns {Promise<SendMessageResult>}
   */
  const sendMessage = useCallback(
    async (text, pageContext) => {
      if (!text || !text.trim()) return { outcome: 'error' };

      const trimmed = text.trim();
      const userMessage = { id: nextMessageId(), role: 'user', content: trimmed };

      setMessages((prev) => [...prev, userMessage]);
      setLoading(true);
      setError(null);

      // Matches the pre-existing behavior: history is built from the
      // messages that existed BEFORE this turn's user message was appended
      // (stale closure read, same as the original implementation).
      const historyForRequest = buildHistory(messages);

      let result;
      if (isStreamingEnabled()) {
        const turnId = generateUuid();
        const streamResult = await attemptStreaming(trimmed, pageContext, historyForRequest, turnId);
        if (streamResult.started) {
          result = { outcome: streamResult.outcome };
        } else {
          result = await sendViaBufferedPath(trimmed, pageContext, historyForRequest);
        }
      } else {
        result = await sendViaBufferedPath(trimmed, pageContext, historyForRequest);
      }

      setLoading(false);
      return result;
    },
    [messages, buildHistory, attemptStreaming, sendViaBufferedPath],
  );

  /**
   * Confirm the pending action by calling /api/tendril/confirm.
   * Clears the pendingAction on success.
   */
  const sendConfirm = useCallback(async () => {
    if (!pendingAction) return;

    setLoading(true);
    setError(null);

    try {
      const sessionTraceparent = getSessionTraceparent();
      const response = await apiClient.post(
        '/api/tendril/confirm',
        {
          conversationId: conversationIdRef.current,
          capabilityName: pendingAction.capabilityName,
        },
        // W3C traceparent sent as an HTTP request header (not in the body).
        sessionTraceparent ? { headers: { traceparent: sessionTraceparent } } : undefined,
      );

      setPendingAction(null);

      setMessages((prev) =>
        prev.map((msg) =>
          msg.pendingAction === pendingAction
            ? { ...msg, pendingAction: null }
            : msg
        )
      );

      const successText = response?.reply || 'Done! Your request was completed.';
      setMessages((prev) => [...prev, { id: nextMessageId(), role: 'assistant', content: successText, outcome: 'ok' }]);
    } catch (err) {
      const errorText = friendlyChatErrorText(err);

      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: 'assistant', content: errorText, isError: true, outcome: 'error' },
      ]);
      setError(errorText);
    } finally {
      setLoading(false);
    }
  }, [pendingAction]);

  /**
   * Cancel the pending action without calling the API.
   */
  const cancelPendingAction = useCallback(() => {
    setPendingAction(null);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.pendingAction ? { ...msg, pendingAction: null } : msg
      )
    );
  }, []);

  /**
   * Start a new conversation — clears messages, clears pending action, and
   * rotates the conversationId so the next message starts a fresh server-side
   * context (the old conversationId's pending action in the Lambda Map is
   * abandoned, which is fine — warm instances recycle eventually).
   */
  const clearChat = useCallback(() => {
    conversationIdRef.current = generateUuid();
    setMessages([]);
    setPendingAction(null);
    setError(null);
  }, []);

  return {
    messages,
    loading,
    error,
    pendingAction,
    // tendril-feedback-capture Phase 2: exposed so FeedbackControls can
    // attach a stable conversationId to each feedback write. Stable across
    // sendMessage/sendConfirm calls; only rotates on clearChat().
    conversationId: conversationIdRef.current,
    sendMessage,
    sendConfirm,
    cancelPendingAction,
    clearChat,
  };
}
